import { createRoute, lazyRouteComponent, stripSearchParams } from '@tanstack/react-router'

import { guardRoute } from '../auth/guard.ts'
import { LEAGUE_SECTIONS, type LeagueSection } from '../data/league-sections.ts'
import { NAV_PARAM_PATHS, NAV_PATHS } from '../navigation/paths.ts'
import { numericIdParam, parseOneOf } from './params.ts'
import { rootRoute } from './root.tsx'

// Neither asks for a role: «public» is every authenticated reader, and the list answers for
// whoever is looking — your gironi, or the season's. Guard map: `guards.test.ts`.

export const leaguesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_PATHS.leagues,
  beforeLoad: ({ context, location }) =>
    guardRoute({ queryClient: context.queryClient, currentPath: location.href }),
  staticData: { opensOnHero: true },
  component: lazyRouteComponent(() => import('../pages/leagues.tsx')),
})

/** A literal and not `leaguesRoute.to`: a link constant is evaluated when the module loads,
 *  and `to` is still empty then — `routes/teams.tsx`'s reason. */
export const leagueListLink = { to: '/leagues' } as const

const groupIdParam = numericIdParam('groupId')

export interface LeagueGroupSearch {
  readonly section: LeagueSection
}

/** The section the girone opens on, which therefore does not belong in the URL. */
const DEFAULT_LEAGUE_SEARCH: LeagueGroupSearch = { section: 'entries' }

export function parseLeagueSection(value: unknown): LeagueSection {
  return parseOneOf(LEAGUE_SECTIONS, value) ?? DEFAULT_LEAGUE_SEARCH.section
}

export const leagueGroupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_PARAM_PATHS.leagueGroup,
  params: groupIdParam,
  validateSearch: (search: Record<string, unknown>): LeagueGroupSearch => ({
    section: parseLeagueSection(search.section),
  }),
  search: {
    middlewares: [stripSearchParams(DEFAULT_LEAGUE_SEARCH)],
  },
  beforeLoad: ({ context, location }) =>
    guardRoute({ queryClient: context.queryClient, currentPath: location.href }),
  staticData: { opensOnHero: true },
  component: lazyRouteComponent(() => import('../pages/league-group.tsx')),
})

/** One section of a girone as a link target. `validateSearch` makes `section` required on
 *  every link here, so it is spelled once. */
export function leagueGroupSectionLink(groupId: number, section: LeagueSection) {
  return {
    to: '/leagues/$groupId',
    params: { groupId },
    search: { section },
  } as const
}

export function leagueGroupLink(groupId: number) {
  return leagueGroupSectionLink(groupId, DEFAULT_LEAGUE_SEARCH.section)
}
